import { assertNever } from '@ofmos/shared/exhaustive';
import type { FieldDiff, FieldMetadata } from '../../data/client';
import { cx, Icon } from '../../ui';
import { fileDiffSentence } from './fileDiffSentence';
import { ReadOnlyFieldValue } from './ReadOnlyFieldValue';

/**
 * WAVE 3 (R-B1): THE FIELD'S NAME IS A ROW LABEL, NOT A HEADING IN CAPITALS, and a change
 * is two lines of plain words rather than a value, a chevron and another value.
 *
 * The uppercase, letter-spaced name and the dash-arrow pair were the two things the owner
 * read as a foreign component inside the rebuilt stream (owner-25: "das design komisch
 * format, die abstaende zeichen und alles"). Neither has an Apple source: the kit's Forms
 * and Lists row masters label a value with the ordinary secondary label, and no Apple part
 * anywhere prints a glyph between two values. The label is therefore the kit's row label -
 * the same `.record-row-label` the field rows above it use, so one screen has one label - and
 * the change reads "Austria" with "Was Germany" under it, which is the sentence a reader was
 * assembling from the arrow anyway.
 * hig: https://developer.apple.com/design/human-interface-guidelines/writing ("Be clear.
 * Choose words that are easily understood ... If you can use fewer words, do so.")
 * kit: Apple macOS 27 UI Kit.sketch / Forms/Example Forms/Light, row label
 *
 * The before/after pairs of one event (INV-4). The event carries real values, so
 * the timeline shows real values - rendered by the same field components as the
 * record itself.
 *
 * The wording depends on what actually happened, and that distinction is the whole
 * point: a refused write did NOT change anything, so calling its second value
 * "after" would be a lie in the audit trail.
 */
export type DiffMode = 'change' | 'refused' | 'proposed';

/** `null` = the two-line form; otherwise the two labels the pair is given. */
function pairLabels(mode: DiffMode): readonly [string, string] | null {
  switch (mode) {
    case 'change':
      return null;
    case 'refused':
      return ['Kept', 'Refused'];
    case 'proposed':
      return ['Current', 'Proposed'];
    default:
      return assertNever(mode, 'FieldDiffList.pairLabels');
  }
}

export interface FieldDiffListProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly fields: readonly FieldMetadata[];
  readonly diff: readonly FieldDiff[];
  readonly mode: DiffMode;
  /**
   * INV-5: fields a human owns, marked ON THE FIELD rather than only summarised below the
   * list. An approver decides field by field, so "one field is locked" is not enough - the
   * mark has to sit next to the value that will not be written.
   */
  readonly lockedFieldIds?: readonly string[] | undefined;
  readonly className?: string | undefined;
}

export function FieldDiffList({
  objectId,
  recordId,
  fields,
  diff,
  mode,
  lockedFieldIds = [],
  className,
}: FieldDiffListProps) {
  const labels = pairLabels(mode);

  return (
    <ul className={cx('space-y-2', className)}>
      {diff.map((entry) => {
        const field = fields.find((candidate) => candidate.id === entry.fieldId);
        const locked = lockedFieldIds.includes(entry.fieldId);
        return (
          <li key={entry.fieldId} className="min-w-0">
            {field === undefined ? (
              // A field that was dropped after the event: the event stays true, so
              // it is shown as-is instead of being hidden or guessed at.
              <p className="text-sm text-ink-muted">
                A field that no longer exists (<span className="font-mono">{entry.fieldId}</span>)
              </p>
            ) : (
              <>
                <p className="record-row-label flex flex-wrap items-center gap-1.5">
                  {field.label}
                  {/*
                    "Human-owned" is NOT rewritten here, although it is jargon by the Writing
                    page's own test. The same words are printed by `pending/MergeFieldRow.tsx`
                    on a screen this round does not rebuild, and one product cannot call one
                    state two things; the phrase goes on the OPEN list for the product-wide
                    copy pass (triage section P).
                  */}
                  {locked && (
                    <span className="inline-flex items-center gap-1 text-warning">
                      <Icon name="blocked" size="0.9rem" />
                      Human-owned
                    </span>
                  )}
                </p>
                {labels === null && field.type === 'FILES' ? (
                  /**
                   * THE ONE PIECE OF DISPLAY WORK D14 CREATES (`file-attachments.md` D14,
                   * section 12). Every other field type renders its before and its after
                   * side by side; two attachment LISTS side by side is a wall nobody reads,
                   * and the question a timeline answers about attachments is "what moved".
                   *
                   * Only in the `change` mode. A refused or a proposed FILES value did NOT
                   * move anything, so "Added Contract.pdf" would be a lie in an audit trail -
                   * those two keep the labelled pair, which renders the lists themselves.
                   */
                  <p className="mt-0.5 text-sm text-ink">
                    {fileDiffSentence(entry.before, entry.after) ?? 'No attachments changed'}
                  </p>
                ) : labels === null ? (
                  /*
                    THE VALUE IT HOLDS NOW, THEN WHAT IT HELD BEFORE (R-B1). The new value
                    leads because it is the fact the record now carries; the old one is the
                    context and reads as a sentence, so nothing has to be inferred from the
                    direction a glyph points in. An empty side is said in words for the same
                    reason - a dash on its own left the reader guessing which of the two
                    values was missing.
                  */
                  <>
                    <p className="mt-0.5 min-w-0 text-sm text-ink">
                      {isBlank(entry.after) ? (
                        'Emptied.'
                      ) : (
                        <ReadOnlyFieldValue
                          objectId={objectId}
                          recordId={recordId}
                          field={field}
                          value={entry.after}
                        />
                      )}
                    </p>
                    {/* The second line only exists where something was replaced. A
                        `created` event carries a diff whose whole before side is empty, and
                        "Nothing was there before" under each of twenty fields is noise a
                        reader has to read past to find the values. */}
                    {!isBlank(entry.before) && (
                      <p className="mt-0.5 flex min-w-0 flex-wrap items-baseline gap-1.5 text-sm text-ink-muted">
                        <span>Was</span>
                        <span className="min-w-0">
                          <ReadOnlyFieldValue
                            objectId={objectId}
                            recordId={recordId}
                            field={field}
                            value={entry.before}
                          />
                        </span>
                      </p>
                    )}
                  </>
                ) : (
                  /*
                    ONE LABEL/VALUE ROW FOR BOTH SIDES, IN THE KIT FORMS ROW'S ANATOMY (R6 C7-19).
                    Each pair was a wrapping flex line, so a value that fitted beside its label
                    stayed there and one that did not dropped under it - the owner's reading on
                    a pending "Change to Larkwood Studio": "Current" in one line with its value,
                    "Proposed" alone with the value below. The pairs now share one
                    grid of a label column and a value column - `.record-row`'s own
                    `auto minmax(0, 1fr)` - so every value starts beside its label, the two
                    values start at the same edge, and a long one wraps inside its column with
                    its first line on the label's baseline, as the Forms row does.
                    kit: Apple macOS 27 UI Kit.sketch / Forms/Example Forms/Light, row
                  */
                  <dl className="mt-0.5 grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-1.5 gap-y-0.5 text-sm">
                    <div className="contents">
                      <dt className="text-sm text-ink-subtle">{labels[0]}</dt>
                      <dd className="min-w-0 text-ink">
                        <ReadOnlyFieldValue
                          objectId={objectId}
                          recordId={recordId}
                          field={field}
                          value={entry.before}
                        />
                      </dd>
                    </div>
                    <div className="contents">
                      <dt className="text-sm text-ink-subtle">{labels[1]}</dt>
                      <dd className="min-w-0 text-ink-muted">
                        <ReadOnlyFieldValue
                          objectId={objectId}
                          recordId={recordId}
                          field={field}
                          value={entry.after}
                        />
                      </dd>
                    </div>
                  </dl>
                )}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * WHETHER A SIDE OF THE PAIR HOLDS ANYTHING, so the entry can say "Emptied" in words
 * instead of printing the tertiary dash a display component draws for nothing.
 *
 * It is deliberately the same test `RecordIdentity` uses for an empty highlight: a value
 * is blank when it is absent, when it is whitespace, or when it is an empty list. `0` and
 * `false` are values and are not blank.
 */
function isBlank(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}
